import { ApprovalableTypeEnum } from 'api-hooks/common';
import { ApprovableType, ModelApprovalable } from 'api-hooks/general/model';
import { ApprovalItemWorkflowEnum } from 'api-hooks/sale/common';
import {
  ExpenseIncomeStatusEnum,
  ProjectStatusEnum,
  PurchaseStatusEnum,
  RefundStatusEnum,
  ReleaseStatusEnum,
  SaleStatusEnum,
  TourStatusEnum,
  TransactionalStatusEnum,
} from 'common/constants/enum';
import notification from 'components/common/notification';
import RequestApprovalButton from 'legacy-modules/approval/approval/components/request-approval-button';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import React from 'react';

import useDialog from './use-dialog';
import useDismiss from './use-dismiss';

export function isDataDraft(
  status:
    | SaleStatusEnum
    | PurchaseStatusEnum
    | TransactionalStatusEnum
    | ReleaseStatusEnum
    | TourStatusEnum
    | ExpenseIncomeStatusEnum
    | ProjectStatusEnum
    | RefundStatusEnum
    | undefined,
) {
  switch (status!) {
    case SaleStatusEnum.RescheduleDraft:
    case SaleStatusEnum.Draft:
    case SaleStatusEnum.VoidDraft:
    case ReleaseStatusEnum.DraftRefund:
    case ReleaseStatusEnum.DraftCancelRefund:
    case SaleStatusEnum.RevertVoidDraft:
    case TourStatusEnum.CancelDraft:
    case TourStatusEnum.CloseDraft:
    case TourStatusEnum.RevertCloseDraft:
      return true;
    case SaleStatusEnum.Rescheduled:
    case SaleStatusEnum.Active:
    case SaleStatusEnum.OpenTicket:
    case SaleStatusEnum.Rescheduling:
    case SaleStatusEnum.PendingApproval:
    case SaleStatusEnum.PendingRescheduleApproval:
    case SaleStatusEnum.PendingVoidApproval:
    case SaleStatusEnum.RejectedApproval:
    case SaleStatusEnum.RejectedRescheduleApproval:
    case SaleStatusEnum.Completed:
    case SaleStatusEnum.Void:
    case SaleStatusEnum.Refund:
    case SaleStatusEnum.Processed:
    case SaleStatusEnum.Submitted:
    case SaleStatusEnum.Waiting:
    case SaleStatusEnum.Archived:
    case PurchaseStatusEnum.Pending:
    case ReleaseStatusEnum.PendingRefund:
    case ReleaseStatusEnum.RejectRefund:
    case ReleaseStatusEnum.PendingCancelRefund:
    case ReleaseStatusEnum.CancelRefund:
    case ReleaseStatusEnum.Requested:
    case ReleaseStatusEnum.PendingReleaseApproval:
    case ReleaseStatusEnum.RejectedReleaseApproval:
    case SaleStatusEnum.PendingRevertVoidApproval:
    case TourStatusEnum.PendingCancelApproval:
    case TourStatusEnum.Canceled:
    case TourStatusEnum.PendingCloseApproval:
    case TourStatusEnum.Closed:
    case TourStatusEnum.PendingRevertCloseApproval:
    default:
      return false;
  }
}

export function approvalMetadata(type: ApprovableType) {
  switch (type) {
    case 'account-payables':
      return {
        approvalableType: ApprovalableTypeEnum.AccountPayable,
        workflow: undefined,
      };
    case 'account-receivables':
      return {
        approvalableType: ApprovalableTypeEnum.AccountReceivable,
        workflow: undefined,
      };
    case 'activity-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.Purchase,
        workflow: ApprovalItemWorkflowEnum.purchase,
      };
    case 'activity-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'balance-adjustments':
      return {
        approvalableType: ApprovalableTypeEnum.BalanceAdjustment,
        workflow: ApprovalItemWorkflowEnum.transactional,
      };
    case 'bank-transactions':
      return {
        approvalableType: ApprovalableTypeEnum.BankTransaction,
        workflow: ApprovalItemWorkflowEnum.transactional,
      };
    case 'cash-transactions':
      return {
        approvalableType: ApprovalableTypeEnum.CashTransaction,
        workflow: ApprovalItemWorkflowEnum.transactional,
      };
    case 'cruise-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.Purchase,
        workflow: ApprovalItemWorkflowEnum.purchase,
      };
    case 'cruise-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'document-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.Purchase,
        workflow: ApprovalItemWorkflowEnum.purchase,
      };
    case 'document-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'flight-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.Purchase,
        workflow: ApprovalItemWorkflowEnum.purchase,
      };
    case 'flight-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'hotel-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.Purchase,
        workflow: ApprovalItemWorkflowEnum.purchase,
      };

    case 'hotel-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'order-adjustments':
      return {
        approvalableType: ApprovalableTypeEnum.OrderAdjustment,
        workflow: ApprovalItemWorkflowEnum.transactional,
      };
    case 'other-expenses':
      return {
        approvalableType: ApprovalableTypeEnum.OtherExpenses,
        workflow: ApprovalItemWorkflowEnum.expense_income,
      };
    case 'other-incomes':
      return {
        approvalableType: ApprovalableTypeEnum.OtherIncome,
        workflow: ApprovalItemWorkflowEnum.expense_income,
      };
    case 'product-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.Purchase,
        workflow: ApprovalItemWorkflowEnum.purchase,
      };
    case 'product-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'receipts':
      return {
        approvalableType: ApprovalableTypeEnum.Receipt,
        workflow: undefined,
      };
    case 'tour-sales':
      return {
        approvalableType: ApprovalableTypeEnum.Sale,
        workflow: ApprovalItemWorkflowEnum.sale,
      };
    case 'transfers':
      return {
        approvalableType: ApprovalableTypeEnum.Transfer,
        workflow: undefined,
      };
    case 'tours':
      return {
        approvalableType: ApprovalableTypeEnum.Tour,
        workflow: ApprovalItemWorkflowEnum.tour,
      };
    case 'projects':
      return {
        approvalableType: ApprovalableTypeEnum.Project,
        workflow: ApprovalItemWorkflowEnum.project,
      };
    case 'refunds':
      return {
        approvalableType: ApprovalableTypeEnum.Refund,
        workflow: ApprovalItemWorkflowEnum.refund,
      };
    case 'release-purchases':
      return {
        approvalableType: ApprovalableTypeEnum.ReleasePurchase,
        workflow: ApprovalItemWorkflowEnum.release_purchase,
      };
    case 'release-sales':
      return {
        approvalableType: ApprovalableTypeEnum.ReleaseSale,
        workflow: ApprovalItemWorkflowEnum.release_sale,
      };
  }
}

export default function useApprovalAction(
  type: ApprovableType,
  data?: ModelApprovalable,
) {
  const dialog = useDialog();

  const id = data?.id;
  const status = data?.status;
  const isDraft = React.useMemo(() => {
    return isDataDraft(status);
  }, [status]);

  const { approvalableType, workflow } = React.useMemo(() => {
    return approvalMetadata(type);
  }, [type]);

  const onDismiss = useDismiss(type, id);

  const onRequestApproval = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? id;

      const message = {
        approvalableType,
        workflow,
        type,
        id: _id || 'undefined',
        message: 'Invalid Request Approval (FE)',
      };

      if (!_id) {
        notification.error({
          message: JSON.stringify(message),
        });
        return;
      }

      dialog.showCustom({
        render: (onClose) => (
          <RequestApprovalModal
            onClose={() => {
              onDismiss(_id);
              onClose();
            }}
            data={{
              approvalableId: _id,
              approvalableType,
              workflow,
            }}
            onDismissSuccess={() => {
              onDismiss(_id);
            }}
          />
        ),
      });
    },
    [approvalableType, dialog, id, onDismiss, type, workflow],
  );

  const requestApprovalPermission = isDraft;

  const requestApprovalButton = requestApprovalPermission && id && (
    <RequestApprovalButton
      data={{
        approvalableId: id,
        approvalableType,
        workflow,
      }}
      onDismiss={onDismiss}
    />
  );

  return {
    onRequestApproval,
    approvalableType,
    workflow,
    requestApprovalButton,
    requestApprovalPermission,
  };
}
